import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  getDefaultModulePermissions,
  normalizeModulePermissions,
} from '../config/modulePermissions';
import { getUserVisibilityPermissions } from '../services/userServices';
import { connectSocket, authenticateSocket, disconnectSocket } from '../services/socket';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modulePermissions, setModulePermissions] = useState(getDefaultModulePermissions());

  const loadUserPermissions = async (userId) => {
    if (!userId) {
      setModulePermissions(getDefaultModulePermissions());
      return;
    }

    try {
      const data = await getUserVisibilityPermissions(userId);
      const normalized = normalizeModulePermissions(data?.permissions);
      const storedUser = JSON.parse(localStorage.getItem('user') || 'null');
      if (storedUser?.role !== 'admin') {
        normalized.users = false;
      }
      setModulePermissions(normalized);
    } catch (error) {
      const defaults = getDefaultModulePermissions();
      const storedUser = JSON.parse(localStorage.getItem('user') || 'null');
      if (storedUser?.role !== 'admin') {
        defaults.users = false;
      }
      setModulePermissions(defaults);
    }
  };

  // Load auth state on app boot
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const token = localStorage.getItem('token');

    if (storedUser && token) {
      const parsedUser = JSON.parse(storedUser);
      setCurrentUser(parsedUser);
      loadUserPermissions(parsedUser.id);
    } else {
      setCurrentUser(null);
      setModulePermissions(getDefaultModulePermissions());
    }

    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 🔥 Listen for forced logout from Axios interceptor
  useEffect(() => {
    const handleLogout = () => {
      logout();
    };

    window.addEventListener('auth:logout', handleLogout);
    return () => window.removeEventListener('auth:logout', handleLogout);
  }, []);

  const login = (user, token) => {
    localStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('token', token);
    setCurrentUser(user);
    loadUserPermissions(user?.id);
    try {
      connectSocket();
      if (token) authenticateSocket(token);
    } catch (e) {
      // non-fatal
      console.warn('Socket auth during login failed', e && e.message ? e.message : e);
    }
  };

  const logout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    setCurrentUser(null);
    setModulePermissions(getDefaultModulePermissions());
    try { disconnectSocket(); } catch (e) { }
  };

  const canAccessModule = (moduleKey) => {
    if (!moduleKey) return true;
    if (String(currentUser?.role || '').toLowerCase() === 'admin') return true;
    if (moduleKey === 'users') return false;
    const normalized = normalizeModulePermissions(modulePermissions);
    return normalized[moduleKey] !== false;
  };

  const updateCurrentUser = (nextUser) => {
    if (!nextUser) return;
    const merged = { ...(currentUser || {}), ...nextUser };
    localStorage.setItem('user', JSON.stringify(merged));
    setCurrentUser(merged);
  };

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        isAuthenticated: !!currentUser,
        login,
        logout,
        loading,
        modulePermissions,
        setModulePermissions,
        loadUserPermissions,
        canAccessModule,
        updateCurrentUser,
      }}
    >
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
